<!--
  Topbar.vue (PR-1 design-parity chain).

  Shared top-chrome component used by DistributorLayout, SuperAdminLayout,
  and PortalLayout. Replaces three near-duplicate ad-hoc topbars with a
  single consistent surface so the design-parity audit can converge on one
  set of tokens, spacings, and data-testids.

  Surface:
    Props:
      - backHref?  (string|null) Optional back-arrow link target. Renders
                   a <router-link> with a Vuetify "arrow_back" icon when
                   provided; omits the element when null/undefined.
      - eyebrow    (string)   Small uppercase label above the title.
      - title      (string)   Primary heading, required. Also the plain-text
                   fallback when the `title` slot is not supplied.
      - saveState? (object|null) Optional autosave indicator. When non-null,
                   shape is `{ label, dotColor, title }` matching
                   useTopBar()'s SAVE_PRESETS. When null, the indicator is
                   omitted.

    Slots:
      - title         Optional rich heading (e.g. DistributorLayout's
                      "<Distributor> > <Page>" with a testid per part).
                      Defaults to the `title` prop text.
      - identityPill  Right-aligned identity affordance (e.g. SUPERADMIN
                      pill, branded client portal pill).
      - viewAs        Generic secondary-affordance slot between the
                      identity pill and the primary action. Currently
                      unused by every layout (its original Distributor
                      "View as" role pill was removed); kept as an
                      extension point and covered by topbar.spec.js.
      - primaryAction Layout-level primary action (e.g. "Save & Publish").
      - default       Catch-all extras (Log out button, etc.).

  data-testids:
    tf-topbar, tf-topbar-back, tf-topbar-eyebrow, tf-topbar-title,
    tf-topbar-save-indicator, tf-topbar-identity-pill, tf-topbar-view-as,
    tf-topbar-primary-action, tf-topbar-extra.

  Tokens: every color / spacing / radius reads from _tokens.css. No
  hardcoded hex values. Vuetify icon set is "md" (Material Design
  ligatures) per main.js -- the back-arrow uses icon="arrow_back", NOT
  any "mdi-*" name (mdi names render as raw text under the md font).
-->
<template>
    <header class="tf-topbar" data-testid="tf-topbar">
        <router-link
            v-if="backHref"
            :to="backHref"
            class="tf-topbar__back"
            data-testid="tf-topbar-back"
            aria-label="Back"
        >
            <v-icon icon="arrow_back" size="20" />
        </router-link>

        <div class="tf-topbar__heading">
            <span
                class="tf-topbar__eyebrow"
                data-testid="tf-topbar-eyebrow"
                >{{ eyebrow }}</span
            >
            <span
                class="tf-topbar__title"
                data-testid="tf-topbar-title"
                ><slot name="title">{{ title }}</slot></span
            >
        </div>

        <div class="tf-topbar__spacer"></div>

        <div
            v-if="saveState"
            class="tf-topbar__save"
            :title="saveState.title"
            data-testid="tf-topbar-save-indicator"
        >
            <span
                class="tf-topbar__save-dot"
                :style="{ background: saveState.dotColor }"
            ></span>
            <span class="tf-topbar__save-label">{{ saveState.label }}</span>
        </div>

        <div
            v-if="$slots.identityPill"
            class="tf-topbar__identity"
            data-testid="tf-topbar-identity-pill"
        >
            <slot name="identityPill" />
        </div>

        <div
            v-if="$slots.viewAs"
            class="tf-topbar__view-as"
            data-testid="tf-topbar-view-as"
        >
            <slot name="viewAs" />
        </div>

        <div
            v-if="$slots.primaryAction"
            class="tf-topbar__primary-action"
            data-testid="tf-topbar-primary-action"
        >
            <slot name="primaryAction" />
        </div>

        <div
            v-if="$slots.default"
            class="tf-topbar__extra"
            data-testid="tf-topbar-extra"
        >
            <slot />
        </div>
    </header>
</template>

<script>
export default {
    name: "Topbar",
    props: {
        backHref: { type: String, default: null },
        eyebrow: { type: String, default: "" },
        title: { type: String, required: true },
        // null = no indicator; otherwise shape {label, dotColor, title}.
        saveState: { type: Object, default: null },
    },
};
</script>

<style scoped>
.tf-topbar {
    /*
     * Fixed-height topbar shell. Layout owners (Distributor / SA / Portal)
     * place this inside their flex column; flex-shrink: 0 prevents it
     * from compressing as the body scrolls.
     */
    display: flex;
    align-items: center;
    gap: 12px;
    height: 56px;
    padding: 0 16px;
    background: var(--tf-bg);
    border-bottom: 1px solid var(--tf-border);
    color: var(--tf-fg-1);
    flex-shrink: 0;
}
.tf-topbar__back {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: var(--tf-radius-xs);
    color: var(--tf-fg-2);
    text-decoration: none;
}
.tf-topbar__back:hover {
    background: var(--tf-surface-alt);
    color: var(--tf-fg-1);
}
.tf-topbar__heading {
    display: flex;
    flex-direction: column;
    line-height: 1.1;
    min-width: 0;
}
.tf-topbar__eyebrow {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 1.2px;
    color: var(--tf-fg-4);
    font-weight: 600;
}
.tf-topbar__title {
    font-size: 16px;
    font-weight: 500;
    color: var(--tf-fg-1);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.tf-topbar__spacer { flex: 1; min-width: 8px; }
.tf-topbar__save {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: var(--tf-fg-3);
}
.tf-topbar__save-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--tf-success);
}
.tf-topbar__identity,
.tf-topbar__view-as,
.tf-topbar__primary-action,
.tf-topbar__extra {
    display: inline-flex;
    align-items: center;
    gap: 8px;
}
</style>
